/**
 * Question Type Time Baselines — Speed normalization for Fluency mode.
 *
 * Different question types inherently require different amounts of time.
 * The baseline is the expected time for a competent learner on that type.
 * Used for speedStreak threshold and speed metric normalization.
 *
 * See OEFENLAB_AUDIT.md §17.3 for full rationale.
 */

import type { QuestionType } from '../../content/types'

const QUESTION_TIME_BASELINES: Record<QuestionType, number> = {
  // Fast (single recognition + click)
  multiple_choice: 8_000,
  true_false_series: 8_000,
  estimation_slider: 10_000,

  // Medium (recall + typed/interactive input)
  fill_in: 12_000,
  clock: 10_000,
  money: 12_000,
  number_sequence: 12_000,
  drag_drop_fill: 12_000,
  error_spotting: 12_000,
  multiplication_grid: 10_000,

  // Procedural (multi-step algorithms)
  cijferend_optellen: 15_000,
  cijferend_aftrekken: 15_000,
  cijferend_vermenigvuldigen: 18_000,
  cijferend_delen: 18_000,

  // Long (multiple operations / visual parsing)
  matching: 18_000,
  ordering: 15_000,
  categorize: 18_000,
  ratio_table: 18_000,
  table_reading: 15_000,
  bar_chart: 15_000,
  line_chart: 15_000,
  pie_chart: 15_000,
  schedule_reading: 15_000,

  // Longest (reading comprehension + selection)
  text_highlight: 25_000,
}

/** Get the speed baseline time (ms) for a question type. */
export function getBaselineTimeMs(questionType: string): number {
  return QUESTION_TIME_BASELINES[questionType as QuestionType] ?? 8_000
}

/** Check if a response qualifies as "fast" for the given question type. */
export function isFastResponse(questionType: string, timeMs: number): boolean {
  return timeMs <= getBaselineTimeMs(questionType)
}

/** Normalize response time relative to baseline (1.0 = at baseline, <1.0 = faster). */
export function normalizeResponseTime(
  questionType: string,
  timeMs: number,
): number {
  return timeMs / getBaselineTimeMs(questionType)
}
